// The HTTP client. One place that knows how to talk to the Go backend:
// base URL, bearer-token attachment, JSON encode/decode, error mapping.
// Everything else (auth.ts, search.ts, future feature modules) is built
// on top of this.
//
// Auth: the Go side accepts `Authorization: Bearer <token>` OR the
// `nocsess` cookie. This client uses the bearer header (the React app
// owns its own token, independent of the PHP-portal session). We also
// send credentials:"include" so that if the app is ever served from the
// same origin as the portal the cookie path keeps working too — belt
// and braces, harmless either way.

import { ApiError } from "./types.js";

export interface ApiClientOptions {
  /** Base URL for the backend. "" → same origin (the Vite dev proxy and
   *  the eventual /app/ deploy both make /v1/* same-origin). */
  baseUrl?: string;
  /** Returns the current bearer token, or null if not logged in. Pulled
   *  fresh on every request so a refresh/logout is picked up immediately. */
  getToken?: () => string | null;
  /** Called when any request comes back 401 — lets the auth layer drop
   *  the token and bounce to /login. */
  onUnauthorized?: () => void;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly getToken: () => string | null;
  private readonly onUnauthorized?: () => void;

  constructor(opts: ApiClientOptions = {}) {
    this.baseUrl = (opts.baseUrl ?? "").replace(/\/$/, "");
    this.getToken = opts.getToken ?? (() => null);
    this.onUnauthorized = opts.onUnauthorized;
  }

  async get<T>(path: string, query?: Record<string, string | number | undefined>): Promise<T> {
    return this.request<T>("GET", path, undefined, query);
  }

  async post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>("POST", path, body);
  }

  async delete<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>("DELETE", path, body);
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    query?: Record<string, string | number | undefined>,
  ): Promise<T> {
    const url = new URL(this.baseUrl + path, window.location.origin);
    if (query) {
      for (const [k, v] of Object.entries(query)) {
        if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, String(v));
      }
    }

    const headers: Record<string, string> = { Accept: "application/json" };
    const token = this.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (body !== undefined) headers["Content-Type"] = "application/json";

    const res = await fetch(url.toString(), {
      method,
      headers,
      credentials: "include",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (res.status === 401) {
      this.onUnauthorized?.();
    }

    const text = await res.text();
    let parsed: unknown = undefined;
    if (text) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = text; // non-JSON body (e.g. plain "unauthorized")
      }
    }

    if (!res.ok) {
      const msg =
        (parsed && typeof parsed === "object" && "error" in parsed && typeof (parsed as { error: unknown }).error === "string"
          ? (parsed as { error: string }).error
          : undefined) ?? `${res.status} ${res.statusText}`;
      throw new ApiError(res.status, msg, parsed);
    }

    return parsed as T;
  }
}
